import React from 'react';
import { router } from '@inertiajs/react';
import type { WikiEditorState } from '../../hooks/useWikiEditor';

// wiki 编辑态的**正文区**：冲突横幅 + markdown 源码框 + 插图入口。
// 不做所见即所得（spec §3.3）。
//
// 状态机不在这里，在 `hooks/useWikiEditor.ts` —— 改版之后「保存 / 完成 /
// 已自动保存」搬到了顶条，而顶条是页面级的，于是同一台状态机有两个渲染者。
// 那个文件的注释论证了为什么状态归页面而不是归这里。
//
// 交互照 DetailPane 的备注卡片（editingNote 那段）：进 textarea、Esc 退出。
type EditorProps = {
  editor: WikiEditorState;
  onDone: () => void;
  // 进来时正文框要不要抢焦点。默认要（点「编辑」进来时下一个动作就是写正文）。
  //
  // 传 false 的只有一处：刚建的、还没被命名的那一页 —— 那时光标该在标题上
  // （见 pages/WikiPage.tsx 的 namingNew）。**这里不能写成硬编码的 autoFocus**，
  // 那样正文框会无条件把焦点从标题抢走，而这个坏法完全静默：页面照常渲染、
  // 照常能编辑，只是光标在错的地方。test/wiki_new_page_flow_test.rb 钉住这一条。
  autoFocus?: boolean;
};

const Editor: React.FC<EditorProps> = ({ editor, onDone, autoFocus = true }) => (
  <div>
    {editor.conflict !== null && (
      <div
        className="mb-3 rounded-lg border border-todo-warnLine bg-todo-warn px-3 py-2.5
                   text-[13px] leading-relaxed text-todo-ink2"
      >
        这页在你编辑期间被改过（现在是第 {editor.conflict} 版）。
        <div className="mt-1.5 flex gap-3">
          <button
            type="button"
            onClick={() => router.reload()}
            className="text-todo-accent hover:underline"
          >
            看看新版本
          </button>
          <button
            type="button"
            onClick={editor.overwrite}
            className="text-todo-accent hover:underline"
          >
            仍然覆盖
          </button>
        </div>
      </div>
    )}

    <textarea
      ref={editor.textareaRef}
      value={editor.body}
      autoFocus={autoFocus}
      onChange={(event) => editor.setBody(event.target.value)}
      onKeyDown={(event) => {
        // Esc 退出，照 DetailPane 的备注卡片。不弹确认框 —— 自动存已经把
        // 改动落库了，Esc 丢掉的至多是最后两秒内的输入。
        if (event.key === 'Escape') onDone();
      }}
      placeholder="正文，markdown，可以直接粘图片"
      className="min-h-[420px] w-full resize-y rounded-lg border border-todo-line bg-todo-card
                 p-4 font-mono text-[13.5px] leading-[1.75] text-todo-ink outline-none
                 placeholder:text-todo-muted"
      {...editor.imageHandlers}
    />

    <div className="mt-3 flex flex-wrap items-center gap-3 text-[12.5px] text-todo-muted">
      <span>markdown 源码 · 可以直接粘图片</span>
      <span className="text-todo-line2">·</span>
      <span>Esc 退出，改动已经自动存下来了</span>
      <span className="text-todo-line2">·</span>
      {/* 移动端没有「粘贴截图」这个动作，但能从相册选（照 DetailPane 的备注卡片）*/}
      <label className="cursor-pointer hover:text-todo-accent">
        插入图片
        <input
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(event) => {
            editor.takeFiles(event.target.files);
            // 不清空的话同一张图连续选第二次不会再触发 onChange
            event.target.value = '';
          }}
        />
      </label>
      {editor.uploading > 0 && <span>上传中…</span>}
      {/* 图片的错误留在正文区：它讲的是「你刚粘的那张图」，而顶条离粘贴的位置
          太远。保存本身的错误在顶条（就在「保存」按钮旁边）。 */}
      {editor.imageError && <span className="text-todo-urgent">{editor.imageError}</span>}
    </div>
  </div>
);

export default Editor;
